import uniqueId from 'lodash/uniqueId';
import React, { useCallback, useState } from 'react';

import Overlay from 'components/Overlay/Overlay';
import CapabilitiesOverlay from 'components/Overlays/CapabilitiesOverlay/CapabilitiesOverlay';
import RichText from 'components/RichText/RichText';
import useOverElement from 'hooks/useOverElement';
import { handleMouseEnter, handleMouseLeave } from 'utils/cursorGlobal';
import {
  ExpertiseCategoryCMS,
  ExpertiseCategoryListItemCMS,
} from 'utils/sharedStrapiQueries/sharedTypes';

import * as S from '../AboutPage.styles';

export interface ExpertiseSectionProps {
  sectionTitle: string;
  categories: Array<{ expertiseCategory: ExpertiseCategoryCMS }>;
}

const ExpertiseSection: React.FunctionComponent<ExpertiseSectionProps> = ({
  sectionTitle,
  categories,
}) => {
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const [category, setCategory] = useState(null);
  const [isOpen, setIsOpen] = useState<boolean>(false);

  const [ref] = useOverElement();

  const setOverlayData = useCallback(
    (
      item: Omit<ExpertiseCategoryCMS, 'categoryTitle' | 'listItems'> &
        ExpertiseCategoryListItemCMS
    ) => {
      setCategory(item);
      setIsOpen(true);
    },
    []
  );

  const categoryLists = categories.map(({ expertiseCategory }) => (
    <S.ExpertiseCategorySublist
      key={uniqueId()}
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
    >
      <S.ExpertiseCategorySublistHeading
        categoryTitleColor={
          expertiseCategory?.categoryTitleColor ||
          expertiseCategory?.categoryTitleDefaultColors
        }
      >
        {expertiseCategory.categoryTitle}
      </S.ExpertiseCategorySublistHeading>

      {expertiseCategory.listItems.map(item => (
        <S.ExpertiseCategorySublistItem key={uniqueId()}>
          <S.ExpertiseCategoryButton
            labelColor={item?.labelColor || item?.labelDefaultColors}
            onClick={() =>
              setOverlayData({
                relatedCaseStudies: expertiseCategory.relatedCaseStudies,
                relatedInsights: expertiseCategory.relatedInsights,
                ...item,
              })
            }
          >
            <span>{item.label}</span>
          </S.ExpertiseCategoryButton>
        </S.ExpertiseCategorySublistItem>
      ))}
    </S.ExpertiseCategorySublist>
  ));

  return (
    <>
      <S.ExpertiseSectionWrapper ref={ref}>
        <S.AboutSectionHeadline>
          <RichText
            as="h3"
            size="heading3"
            richText={sectionTitle}
            defaultFormatting
          />
        </S.AboutSectionHeadline>

        <S.ExpertiseCategoryList>{categoryLists}</S.ExpertiseCategoryList>
      </S.ExpertiseSectionWrapper>

      <Overlay isOpen={isOpen} onExitClick={() => setIsOpen(false)}>
        <CapabilitiesOverlay {...category} />
      </Overlay>
    </>
  );
};

export default ExpertiseSection;
